import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import TermsNote from "../../components/TermsNote";
import PurchaseChecks, { purchaseChecked } from "../../components/PurchaseChecks";
import { Spinner } from "../../components/Loader";
import { getSiteOffer } from "../../services/sites";
import { checkPlanCoupon } from "../../services/coupons";
import { errorMessage } from "../../services";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Choose what to pay for. The server works out the price from the plans. A store owner ticks the Terms and plans
// boxes first, and sees the first-month offer when it applies to them (one month of the monthly plan).
export default function PayModal({ site, isSuperAdmin, onSubmit, onClose }) {
  const [plan, setPlan] = useState("monthly");
  const [months, setMonths] = useState(1);
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState({ terms: false, plans: false });
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) return undefined;
    let live = true;
    getSiteOffer(site.id).then(({ data }) => live && setOffer(data.offer)).catch(() => {});
    return () => { live = false; };
  }, [site.id, isSuperAdmin]);
  const offerApplies = offer && plan === "monthly" && Number(months) === 1;
  // An Ownezzy coupon (store owners; not with the first-month offer): checked here, applied again by the server
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, summary, discount }
  const [couponError, setCouponError] = useState("");
  const [checking, setChecking] = useState(false);
  useEffect(() => { setCoupon(null); setCouponError(""); }, [plan, months]); // the discount depends on both
  const applyCoupon = async () => {
    if (!code.trim()) return;
    setChecking(true);
    setCouponError("");
    try {
      const { data } = await checkPlanCoupon(site.id, { code: code.trim(), plan, months: plan === "yearly" ? 12 : Number(months) });
      setCoupon(data.coupon);
    } catch (err) {
      setCoupon(null);
      setCouponError(errorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(site, { plan, months: plan === "yearly" ? 12 : Number(months), ...(coupon ? { couponCode: coupon.code } : {}) });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal eyebrow="SUBSCRIPTION" title={isSuperAdmin ? `Record payment · ${site.name}` : `Pay now · ${site.name}`} onClose={onClose}
      description={isSuperAdmin ? "Records a payment you have received and activates the plan." : "Pay by UPI with Razorpay. The plan activates as soon as the payment goes through."}>
      <form onSubmit={submit}>
        <label className="admin-field">Plan
          <select value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </label>
        {plan === "monthly" && (
          <label className="admin-field">Months
            <select value={months} onChange={(e) => setMonths(e.target.value)}>
              {[1, 2, 3, 6, 12].map((m) => <option key={m} value={m}>{m} {m === 1 ? "month" : "months"}</option>)}
            </select>
          </label>
        )}
        {offerApplies && (
          <div className="pay-offer">🎉 First-month offer: <b>{rupees(offer.price)}</b> for your first month, then {rupees(offer.fullPrice)}/month.</div>
        )}
        {offer && !offerApplies && plan === "monthly" && (
          <small className="field-help">Choose 1 month to get your first month for {rupees(offer.price)}.</small>
        )}
        {!isSuperAdmin && !offerApplies && !site.franchiseId && (
          <div className="pay-coupon">
            <label className="admin-field">Coupon code (optional)
              <span className="pay-coupon-row">
                <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCoupon(null); }} placeholder="e.g. WEB20" maxLength={30} />
                <button type="button" className="outline-btn" disabled={checking || !code.trim()} onClick={applyCoupon}>{checking ? <Spinner /> : "Apply"}</button>
              </span>
            </label>
            {coupon && <div className="pay-coupon-ok">✓ {coupon.code}: {coupon.summary}, ₹{coupon.discount.toLocaleString("en-IN")} off (before GST)</div>}
            {couponError && <div className="pay-coupon-error">{couponError}</div>}
          </div>
        )}
        {!isSuperAdmin && <PurchaseChecks checks={checks} onChange={setChecks} />}
        {!isSuperAdmin && <TermsNote />}
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy || (!isSuperAdmin && !purchaseChecked(checks))}>{busy ? <><Spinner /> Please wait</> : isSuperAdmin ? "Record payment" : "Continue to payment"}</button>
        </div>
      </form>
    </Modal>
  );
}
